<script lang="ts" setup>
import type { GuestListFilters } from "@/components/GuestListFiltersDrawer/types"
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import { refDebounced, useMediaQuery } from "@vueuse/core"
import { computed, onMounted, reactive, ref, shallowRef, watchEffect } from "vue"
import { useListGuests } from "@/api"
import { DataCard, GuestListFiltersDrawer } from "@/components"
import { Badge, Button, ButtonGroup, Icon, Input, Pagination, Table } from "@/components/ui"
import { useConvertedRouteParam, useModal, usePagination, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { GuestListFiltersDrawerKey } from "@/symbols"
import { ViewMode } from "@/utils"

definePage({
  meta: {
    requiresAuth: true,
  },
})

interface GuestTableRow {
  name: string
  email?: string
  picsCount?: number
  vidsCount?: number
  messagesCount?: number
  actions?: null
}

const filters = reactive({
  page: 1,
  perPage: 1,
  search: "",
})

const search = shallowRef("")
const debouncedSearch = refDebounced(search, 500)

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/guest-list", value => Number(value))

const { data, isLoading, pageCount } = useListGuests(eventVaultId, filters)
const { t } = useTranslation()
const { toggle: toggleFilters } = useModal(GuestListFiltersDrawerKey)
const pagination = usePagination({ initialPerPage: 10, pageCount })

const mode = ref<ViewMode>(ViewMode.TABLE)

const isMobileView = useMediaQuery("(max-width: 1024px)")

onMounted(() => {
  if (isMobileView.value) {
    mode.value = ViewMode.CARD
  }
})

const guests = computed<GuestTableRow[]>(() => {
  return (
    data.value?.map(guest => ({
      name: guest.name ?? t("guest_list_page.unknown_guest"),
      email: guest.email,
      picsCount: guest.picsCount,
      vidsCount: guest.vidsCount,
      messagesCount: guest.messagesCount,
      actions: null,
    })) ?? []
  )
})

const headers = computed<ITableHeader<GuestTableRow>[]>(() => [
  { label: t("guest_list_page.headers.name"), value: "name" },
  { label: t("guest_list_page.headers.email"), value: "email" },
  { label: t("guest_list_page.headers.pic_count"), value: "picsCount" },
  { label: t("guest_list_page.headers.vid_count"), value: "vidsCount" },
  { label: t("guest_list_page.headers.message_count"), value: "messagesCount" },
])

function onFiltersUpdate(values: GuestListFilters) {
  if (values.perPage)
    filters.perPage = values.perPage
}

function onPaginationNext() {
  pagination.next()
  filters.page = pagination.page.value
}

function onPaginationPrevious() {
  pagination.previous()
  filters.page = pagination.page.value
}

watchEffect(() => {
  filters.search = debouncedSearch.value
  pagination.page.value = 1
  filters.page = 1
})

const filtersCount = computed(() => {
  let count = 0

  if (filters.perPage !== 10)
    count++

  return count
})
</script>

<template>
  <SidebarHostEventLayout>
    <div class="mb-5">
      <div class="flex items-center justify-between gap-5 mb-3">
        <h2 class="text-[#072A47] text-2xl font-comfortaa font-bold whitespace-nowrap">
          {{ t('guest_list_page.title') }}
        </h2>
        <div class="max-w-72">
          <Input
            v-model="search" name="search" :label="t('guest_list_page.search_label')"
            label-icon="Search" clearable
          />
        </div>
      </div>
      <div class="flex items-center justify-end gap-2 w-full">
        <ButtonGroup>
          <Button
            data-test="table_view_button" :variant="mode === ViewMode.TABLE ? 'tertiary' : 'primary'"
            :disabled="mode === ViewMode.TABLE" @click="mode = ViewMode.TABLE"
          >
            <Icon icon="Table" />
            <span class="hidden md:block">{{ t("host_dashboard.table_view") }}</span>
            <span class="sr-only">{{ t("host_dashboard.select_table_view") }}</span>
          </Button>
          <Button
            data-test="card_view_button" :variant="mode === ViewMode.CARD ? 'tertiary' : 'primary'"
            :disabled="mode === ViewMode.CARD" @click="mode = ViewMode.CARD"
          >
            <Icon icon="Cards" />
            <span class="hidden md:block">{{ t("host_dashboard.card_view") }}</span>
            <span class="sr-only">{{ t("host_dashboard.select_card_view") }}</span>
          </Button>
        </ButtonGroup>
        <Badge :count="filtersCount">
          <Button prefix-icon="Filter" variant="icon" @click="toggleFilters">
            <span class="sr-only md:not-sr-only">{{ t('filter') }}</span>
          </Button>
        </Badge>
      </div>
    </div>
    <Transition>
      <Table
        v-if="mode === ViewMode.TABLE"
        :headers="headers"
        :data="guests"
        :is-loading="isLoading"
        :pagination="pagination"
        show-pagination
        @pagination-next="() => onPaginationNext()"
        @pagination-previous="() => onPaginationPrevious()"
      />
      <div v-else>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <DataCard v-for="(guest, index) in guests" :key="`guest-${index}`" :headers="headers" :data="guest" />
        </div>
        <Pagination
          :current-page="pagination.page.value"
          :page-count="pagination.pageCount?.value"
          :per-page="pagination.perPage.value"
          :can-next="pagination.canNext.value"
          :can-previous="pagination.canPrevious.value"
          @next="() => onPaginationNext()"
          @previous="() => onPaginationPrevious()"
        />
      </div>
    </Transition>
    <GuestListFiltersDrawer :filters="filters" @update:filters="onFiltersUpdate" />
  </SidebarHostEventLayout>
</template>
